import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Known modal IDs. The `(string & {})` trick allows any string while keeping
 * autocomplete for the named literals in editors and AI tools.
 */
export type ModalId =
  | 'feedbackModal'
  | 'journalPreview'
  | 'sessionExpired'
  | 'consentRequired'
  | (string & {}); // NOSONAR: intentional LiteralUnion trick — see comment above, not dead code

interface UIState {
  /** Currently open modal, or null if none */
  activeModal: ModalId | null;
  /** Arbitrary data passed when opening a modal (e.g. a record ID) */
  modalData: Record<string, unknown> | null;
  /** Whether a full-page loading overlay is active */
  isGlobalLoading: boolean;
  /** Whether the mobile sidebar drawer is open */
  isSidebarOpen: boolean;
}

interface UIActions {
  openModal: (id: ModalId, data?: Record<string, unknown>) => void;
  closeModal: () => void;
  setGlobalLoading: (loading: boolean) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  /** Reset all UI state to defaults (call on logout or route change) */
  resetUI: () => void;
}

// ─── Initial state ────────────────────────────────────────────────────────────

const initialState: UIState = {
  activeModal: null,
  modalData: null,
  isGlobalLoading: false,
  isSidebarOpen: false,
};

// ─── Store ────────────────────────────────────────────────────────────────────

export const useUIStore = create<UIState & UIActions>()(
  devtools(
    (set) => ({
      ...initialState,

      openModal: (id, data?) =>
        set(
          { activeModal: id, modalData: data ?? null },
          false,
          'ui/openModal'
        ),

      closeModal: () =>
        set({ activeModal: null, modalData: null }, false, 'ui/closeModal'),

      setGlobalLoading: (loading) =>
        set({ isGlobalLoading: loading }, false, 'ui/setGlobalLoading'),

      toggleSidebar: () =>
        set(
          (s) => ({ isSidebarOpen: !s.isSidebarOpen }),
          false,
          'ui/toggleSidebar'
        ),

      setSidebarOpen: (open) =>
        set({ isSidebarOpen: open }, false, 'ui/setSidebarOpen'),

      resetUI: () => set(initialState, false, 'ui/resetUI'),
    }),
    { name: 'MamBaby/ui' }
  )
);
